import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LanguageIcon from '@mui/icons-material/Language';
import SpeedIcon from '@mui/icons-material/Speed';
import PaymentsIcon from '@mui/icons-material/Payments';
import InsightsIcon from '@mui/icons-material/Insights';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import { Link as RouterLink } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';

const benefits = [
  {
    icon: <PointOfSaleIcon />,
    title: 'Vente rapide',
    text: 'Encaissez en quelques secondes avec un mode caisse simple et clair.',
  },
  {
    icon: <Inventory2Icon />,
    title: 'Stock sous controle',
    text: 'Suivez les quantites et les prix en temps reel sans Excel.',
  },
  {
    icon: <PaymentsIcon />,
    title: 'Credits partenaires',
    text: 'Gerez les partenaires qui paient plus tard sans confusion.',
  },
  {
    icon: <InsightsIcon />,
    title: 'Vision business',
    text: 'Consultez ventes, depenses et performance dans un seul tableau.',
  },
];

const whyUs = [
  {
    icon: <BoltIcon />,
    title: 'Simple pour toute equipe',
    text: 'Pas besoin de competences techniques pour commencer.',
  },
  {
    icon: <PhoneIphoneIcon />,
    title: '100% mobile friendly',
    text: 'Utilisable confortablement sur telephone en boutique.',
  },
  {
    icon: <LanguageIcon />,
    title: 'Francais + arabe',
    text: 'Ideal pour des equipes et clients multilingues.',
  },
  {
    icon: <SupportAgentIcon />,
    title: 'Pense pour le terrain',
    text: 'Construit pour les besoins reels des commerces de proximite.',
  },
];

const steps = [
  'Ajoutez vos articles et votre stock initial.',
  'Vendez en caisse et validez le panier en fin de vente.',
  'Pilotez vos resultats avec des donnees claires chaque jour.',
];

export default function Landing() {
  const { user } = useContext(AuthContext);
  const whatsappUrl = 'https://wa.me/22200000000?text=Bonjour%20je%20veux%20une%20demo%20Mousaidi';

  return (
    <Box>
      <Box sx={{ py: { xs: 4, md: 7 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Paper
                sx={{
                  p: { xs: 3, md: 4 },
                  minHeight: '100%',
                  background: 'var(--surface-1)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                <Stack spacing={2.4}>
                  <Chip
                    icon={<StorefrontIcon fontSize="small" />}
                    label="Solution complete pour magasins"
                    sx={{ width: 'fit-content', fontWeight: 800 }}
                  />
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.05,
                      fontSize: { xs: '2.1rem', md: '3rem' },
                    }}
                  >
                    Vendez mieux. Gerez mieux. Grandissez plus vite.
                  </Typography>
                  <Typography sx={{ color: 'var(--text-secondary)', maxWidth: 680 }}>
                    Mousaidi aide les commerces a gerer la caisse, les articles, le stock et le suivi financier
                    dans une interface moderne et facile a utiliser.
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button component={RouterLink} to={user ? '/register' : '/login'} variant="contained" size="large">
                      {user ? 'Aller a la vente' : 'Demarrer maintenant'}
                    </Button>
                    <Button component={RouterLink} to="/login" variant="outlined" size="large">
                      Voir une demo
                    </Button>
                  </Stack>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip icon={<SpeedIcon fontSize="small" />} label="Mise en place rapide" />
                    <Chip icon={<LanguageIcon fontSize="small" />} label="FR / AR" />
                    <Chip icon={<PublicIcon fontSize="small" />} label="Cloud et multi-appareils" />
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack spacing={2}>
                <Paper sx={{ p: 2.2, background: 'var(--surface-2)', border: '1px solid var(--border-subtle)' }}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <CheckCircleOutlineIcon />
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>Concu pour les boutiques</Typography>
                      <Typography sx={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                        Epiceries, discount stores, magasins de quartier.
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
                <Paper sx={{ p: 2.2, background: 'var(--surface-2)', border: '1px solid var(--border-subtle)' }}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <PointOfSaleIcon />
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>Focus caisse</Typography>
                      <Typography sx={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                        Le caissier voit uniquement ce qu il faut pour vendre vite.
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
                <Paper sx={{ p: 2.2, background: 'var(--surface-2)', border: '1px solid var(--border-subtle)' }}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <InsightsIcon />
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>Decision basee sur donnees</Typography>
                      <Typography sx={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                        Suivez votre activite et identifiez vite ce qui fonctionne.
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 5 } }}>
        <Paper sx={{ p: { xs: 2.5, md: 3.5 }, background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 1.5 }}>
            Les avantages cles
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 2.5 }}>
            Une application pratique qui reduit les erreurs et fait gagner du temps chaque jour.
          </Typography>
          <Grid container spacing={2}>
            {benefits.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.title}>
                <Paper sx={{ p: 2, height: '100%', background: 'var(--surface-2)', border: '1px solid var(--border-subtle)' }}>
                  <Stack spacing={1.1}>
                    <Box sx={{ color: 'var(--text-primary)' }}>{item.icon}</Box>
                    <Typography sx={{ fontWeight: 800 }}>{item.title}</Typography>
                    <Typography sx={{ color: 'var(--text-secondary)', fontSize: 14 }}>{item.text}</Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 5 } }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2.5, md: 3.5 }, height: '100%', background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}>
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1.5 }}>
                Pourquoi Mousaidi
              </Typography>
              <Stack spacing={1.3}>
                {whyUs.map((row) => (
                  <Stack key={row.title} direction="row" spacing={1.1} alignItems="flex-start">
                    <Box sx={{ mt: 0.2 }}>{row.icon}</Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, mb: 0.2 }}>{row.title}</Typography>
                      <Typography sx={{ color: 'var(--text-secondary)', fontSize: 14 }}>{row.text}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2.5, md: 3.5 }, height: '100%', background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}>
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1.5 }}>
                Comment ca marche
              </Typography>
              <Stack spacing={1.2}>
                {steps.map((step, idx) => (
                  <Paper key={step} sx={{ p: 1.4, background: 'var(--surface-2)', border: '1px solid var(--border-subtle)' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{idx + 1}. {step}</Typography>
                  </Paper>
                ))}
              </Stack>
              <Box sx={{ mt: 2 }}>
                <Button component={RouterLink} to="/login" variant="contained" size="large" fullWidth>
                  Commencer une demonstration
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Box
        component="footer"
        sx={{
          borderTop: '1px solid var(--border-subtle)',
          background: 'linear-gradient(180deg, rgba(6,95,70,0.18), rgba(6,95,70,0.06))',
          py: 3.5,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography sx={{ fontWeight: 900, fontSize: 20 }}>MOUSAIDI</Typography>
              <Typography sx={{ color: 'var(--text-secondary)', fontSize: 14, mt: 0.6 }}>
                Plateforme de gestion pour commerces modernes.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack spacing={0.8}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EmailIcon fontSize="small" />
                  <Typography sx={{ fontSize: 14 }}>contact@mousaidi.com</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PhoneIcon fontSize="small" />
                  <Typography sx={{ fontSize: 14 }}>+222 00 00 00 00</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <WhatsAppIcon fontSize="small" />
                  <Typography sx={{ fontSize: 14 }}>WhatsApp support rapide</Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction={{ xs: 'row', md: 'column' }} spacing={1.1}>
                <Button component={RouterLink} to="/login" size="small" variant="outlined">
                  Connexion
                </Button>
                <Button component={RouterLink} to="/login" size="small" variant="contained">
                  Demander une demo
                </Button>
              </Stack>
            </Grid>
          </Grid>
          <Typography sx={{ color: 'var(--text-muted)', fontSize: 12.5, mt: 2.5 }}>
            © {new Date().getFullYear()} Mousaidi. Tous droits reserves.
          </Typography>
        </Container>
      </Box>

      <IconButton
        component="a"
        href={whatsappUrl}
        target="_blank"
        rel="noreferrer"
        aria-label="WhatsApp"
        sx={{
          position: 'fixed',
          right: 18,
          bottom: 18,
          width: 56,
          height: 56,
          zIndex: 1300,
          color: '#ffffff',
          background: 'linear-gradient(160deg, #22c55e, #16a34a)',
          border: '1px solid rgba(255,255,255,0.28)',
          boxShadow: '0 12px 24px rgba(22,163,74,0.38)',
          '&:hover': {
            background: 'linear-gradient(160deg, #16a34a, #15803d)',
          },
        }}
      >
        <WhatsAppIcon />
      </IconButton>
    </Box>
  );
}
